import React, { useState, useEffect } from 'react';
import { X, Link2, ArrowRight, Edit3 } from 'lucide-react';
import Button from '../ui/Button';

interface EditRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (relationshipData: EditRelationshipData) => void;
  relationshipData: {
    edgeId: string;
    relationshipType: string;
    sourceLabel: string;
    targetLabel: string;
    cardinality?: string;
    description?: string;
  } | null;
}

export interface EditRelationshipData {
  edgeId: string;
  relationshipType: string;
  cardinality: string;
  description: string;
}

const CARDINALITY_OPTIONS = [
  { value: 'one_to_one', label: '1:1', description: 'One to One' },
  { value: 'one_to_many', label: '1:N', description: 'One to Many' },
  { value: 'many_to_one', label: 'N:1', description: 'Many to One' },
  { value: 'many_to_many', label: 'N:N', description: 'Many to Many' },
];

const COMMON_RELATIONSHIP_TYPES = [
  'HAS',
  'BELONGS_TO',
  'CONTAINS',
  'REFERENCES',
  'CREATED_BY',
  'MANAGES',
  'OWNS',
  'SUPPLIES',
  'PRODUCES',
  'SHIPS_TO',
  'REPORTS_TO',
  'ASSOCIATED_WITH',
];

export const EditRelationshipModal: React.FC<EditRelationshipModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  relationshipData,
}) => {
  const [relationshipType, setRelationshipType] = useState('');
  const [cardinality, setCardinality] = useState('one_to_many');
  const [description, setDescription] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Initialize form when modal opens with relationship data
  useEffect(() => {
    if (isOpen && relationshipData) {
      setRelationshipType(relationshipData.relationshipType || '');
      setCardinality(relationshipData.cardinality || 'one_to_many');
      setDescription(relationshipData.description || '');
    }
  }, [isOpen, relationshipData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!relationshipData || !relationshipType.trim()) {
      return;
    }

    onConfirm({
      edgeId: relationshipData.edgeId,
      relationshipType: relationshipType.toUpperCase().replace(/\s+/g, '_'),
      cardinality,
      description: description || `${relationshipData.sourceLabel} ${relationshipType} ${relationshipData.targetLabel}`,
    });
  };

  const handleSuggestionClick = (type: string) => {
    setRelationshipType(type);
    setShowSuggestions(false);
  };

  if (!isOpen || !relationshipData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-purple-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Link2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">
                Edit Relationship
              </h2>
              <p className="text-sm text-neutral-500">
                Modify the connection
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Connection Preview */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-neutral-200">
          <div className="flex items-center justify-center gap-3">
            <div className="px-3 py-2 bg-white rounded-lg border border-blue-200 shadow-sm">
              <span className="font-medium text-blue-700">
                {relationshipData.sourceLabel}
              </span>
            </div>
            <ArrowRight className="w-5 h-5 text-purple-500" />
            <div className="px-3 py-2 bg-white rounded-lg border border-purple-200 shadow-sm">
              <span className="font-medium text-purple-700">
                {relationshipData.targetLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Relationship Type */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Relationship Type <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value.toUpperCase())}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="e.g., HAS, BELONGS_TO, CONTAINS"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase"
                autoFocus
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {COMMON_RELATIONSHIP_TYPES.filter((type) =>
                    type.includes(relationshipType.toUpperCase())
                  ).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleSuggestionClick(type)}
                      className="w-full px-4 py-2 text-left hover:bg-neutral-50 text-sm font-mono"
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Cardinality */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Cardinality
            </label>
            <div className="grid grid-cols-4 gap-2">
              {CARDINALITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setCardinality(option.value)}
                  className={`px-3 py-2 rounded-lg border text-center transition-colors ${
                    cardinality === option.value
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-neutral-200 hover:border-neutral-300 text-neutral-600'
                  }`}
                >
                  <div className="font-mono font-bold text-sm">{option.label}</div>
                  <div className="text-[10px] text-neutral-500">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Description <span className="text-neutral-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this relationship..."
              rows={2}
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!relationshipType.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRelationshipModal;
